import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase';

function AddSchedule({ onClose, devices, menuItems, currentSchedule }) {
  const [deviceId, setDeviceId] = useState('');
  const [scheduledMenuItemId, setScheduledMenuItemId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [days, setDays] = useState([]);
  const [isRecurring, setIsRecurring] = useState(true);
  // New fields for fixed (one-time) schedules
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    if (currentSchedule) {
      setDeviceId(currentSchedule.deviceId);
      setScheduledMenuItemId(currentSchedule.scheduledMenuItemId);
      setDays(currentSchedule.days || []);

      // Determine if this is a recurring or fixed schedule
      const hasDateTimeFields = currentSchedule.startDateTime && currentSchedule.endDateTime;
      setIsRecurring(!hasDateTimeFields);

      if (hasDateTimeFields) {
        // Fixed schedule - use datetime fields
        setStartDateTime(currentSchedule.startDateTime);
        setEndDateTime(currentSchedule.endDateTime);
        setStartTime('');
        setEndTime('');
      } else {
        // Recurring schedule - use time fields
        setStartTime(currentSchedule.startTime || '');
        setEndTime(currentSchedule.endTime || '');
        setStartDateTime('');
        setEndDateTime('');
      }
    }
  }, [currentSchedule]);

  const handleDayToggle = (day) => {
    if (days.includes(day)) {
      setDays(days.filter(d => d !== day));
    } else {
      setDays([...days, day]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!deviceId || !scheduledMenuItemId) {
      alert('Please fill in all required fields.');
      return;
    }

    // Validate based on schedule type
    if (isRecurring) {
      if (!startTime || !endTime) {
        alert('Please fill in start and end times.');
        return;
      }
      if (startTime >= endTime) {
        alert('End time must be after start time.');
        return;
      }
    } else {
      if (!startDateTime || !endDateTime) {
        alert('Please fill in start and end date/time.');
        return;
      }
      if (startDateTime >= endDateTime) {
        alert('End date/time must be after start date/time.');
        return;
      }
    }

    // Get device info for display
    const device = devices.find(d => d.deviceId === deviceId);
    const menu = menuItems.find(m => m.id === scheduledMenuItemId);

    // Build schedule data based on type
    const scheduleData = {
      deviceId,
      deviceName: device?.deviceName || '',
      scheduledMenuItemId,
      scheduledMenuTitle: menu?.title || '',
      isRecurring,
      isActive: false,
      originalMenuItemId: null, // Will be set when schedule activates
    };

    if (isRecurring) {
      // Recurring schedule: use days + time fields
      scheduleData.startTime = startTime;
      scheduleData.endTime = endTime;
      scheduleData.days = days.length === 0 ? [] : days; // Empty array means every day
      // Clear datetime fields if they existed before
      scheduleData.startDateTime = null;
      scheduleData.endDateTime = null;
    } else {
      // Fixed schedule: use datetime fields
      scheduleData.startDateTime = startDateTime;
      scheduleData.endDateTime = endDateTime;
      // Clear recurring fields
      scheduleData.startTime = null;
      scheduleData.endTime = null;
      scheduleData.days = null;
    }

    if (currentSchedule) {
      // Update existing schedule
      await firestore.collection('schedules').doc(currentSchedule.id).update(scheduleData);
    } else {
      // Add new schedule
      await firestore.collection('schedules').add({
        ...scheduleData,
        createdAt: new Date().toISOString(),
      });
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">
          {currentSchedule ? 'Edit Schedule' : 'Add Schedule'}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-1 font-medium">Device *</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              required
            >
              <option value="">-- Select Device --</option>
              {devices.map(device => (
                <option key={device.id} value={device.deviceId}>
                  {device.deviceName} ({device.storeCode}) - {device.screenPosition || 'No position'}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block mb-1 font-medium">Scheduled Menu *</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={scheduledMenuItemId}
              onChange={(e) => setScheduledMenuItemId(e.target.value)}
              required
            >
              <option value="">-- Select Menu to Display --</option>
              {menuItems.map(item => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">
              This menu will be shown during the scheduled time
            </p>
          </div>

          <div className="mb-4">
            <label className="block mb-2 font-medium">Schedule Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={isRecurring}
                  onChange={() => setIsRecurring(true)}
                  className="h-4 w-4"
                />
                <span>Recurring (weekly)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!isRecurring}
                  onChange={() => setIsRecurring(false)}
                  className="h-4 w-4"
                />
                <span>One-time / Fixed</span>
              </label>
            </div>
          </div>

          {isRecurring ? (
            <>
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium">Start Time *</label>
                  <input
                    type="time"
                    className="w-full border px-3 py-2 rounded"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 font-medium">End Time *</label>
                  <input
                    type="time"
                    className="w-full border px-3 py-2 rounded"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-2 font-medium">Days of Week</label>
                <div className="grid grid-cols-4 gap-2">
                  {weekDays.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`px-3 py-2 rounded text-sm ${
                        days.includes(day)
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day.substring(0, 3)}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {days.length === 0 ? 'Schedule will run every day' : `Selected: ${days.length} day(s)`}
                </p>
              </div>
            </>
          ) : (
            <div className="mb-4 space-y-4">
              <div>
                <label className="block mb-1 font-medium">Start Date & Time *</label>
                <input
                  type="datetime-local"
                  className="w-full border px-3 py-2 rounded"
                  value={startDateTime}
                  onChange={(e) => setStartDateTime(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block mb-1 font-medium">End Date & Time *</label>
                <input
                  type="datetime-local"
                  className="w-full border px-3 py-2 rounded"
                  value={endDateTime}
                  onChange={(e) => setEndDateTime(e.target.value)}
                  required
                />
              </div>
              <p className="text-sm text-gray-500">
                This schedule will only run once during the specified date and time range.
              </p>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> The device will automatically switch to the scheduled menu at the start time
              and revert to its current menu at the end time. Make sure your backend function is running.
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-teal-500 text-white px-4 py-2 rounded"
            >
              {currentSchedule ? 'Update' : 'Create'} Schedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddSchedule;
